import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

/**
 * Themed mailbox picker; replaces native select for sidebar.
 */
export default function MailboxDropdown({ mailboxes, value, onChange, disabled, id, collapsed }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const active = mailboxes.find((m) => m.hash === value);
  const label = active
    ? active.label || active.email || active.hash || "Mailbox"
    : mailboxes.length
      ? "Mailbox"
      : "No mailbox connected";

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        id={id}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Active mailbox"
        title={label}
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`flex w-full items-center justify-between gap-2 rounded-lg border border-theme-border bg-theme-panel/40 px-2.5 py-2 text-left font-mono text-[11px] text-theme-text shadow-sm transition-colors hover:border-theme-border-bright hover:bg-theme-panel/70 focus:outline-none focus:ring-1 focus:ring-theme-accent/60 disabled:pointer-events-none disabled:opacity-50 ${
          collapsed ? "md:justify-center md:px-2" : ""
        }`}
      >
        <span className={`min-w-0 flex-1 truncate ${collapsed ? "md:sr-only" : ""}`}>{label}</span>
        <span
          className={`material-symbols-outlined shrink-0 text-[18px] leading-none text-theme-dim ${
            collapsed ? "md:mx-0" : ""
          }`}
        >
          {open ? "expand_less" : "expand_more"}
        </span>
      </button>
      {open && !disabled ? (
        <ul
          className="absolute left-0 right-0 top-full z-[200] mt-1 max-h-56 list-none overflow-y-auto rounded-lg border border-theme-border bg-theme-main py-1 shadow-[0_12px_40px_rgba(0,0,0,0.18)]"
          role="listbox"
          aria-label="Mailbox options"
        >
          {mailboxes.map((m) => {
            const itemLabel = m.label || m.email || m.hash;
            const selected = m.hash === value;
            return (
              <li key={m.hash} role="none">
                <button
                  type="button"
                  role="option"
                  aria-selected={selected}
                  className={`flex w-full items-center gap-2 px-3 py-2.5 text-left font-mono text-[11px] transition-colors hover:bg-theme-panel ${
                    selected ? "bg-theme-panel text-theme-accent" : "text-theme-text"
                  }`}
                  onClick={() => {
                    onChange(m.hash);
                    setOpen(false);
                  }}
                >
                  <span
                    className="inline-flex w-5 shrink-0 items-center justify-center"
                    aria-hidden="true"
                  >
                    {selected ? (
                      <span className="material-symbols-outlined text-[16px] leading-none text-theme-accent">
                        check
                      </span>
                    ) : null}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{itemLabel}</span>
                </button>
              </li>
            );
          })}
          {mailboxes.length > 0 ? (
            <li role="separator" aria-hidden="true">
              <div className="mx-3 my-1 border-t border-theme-border" />
            </li>
          ) : null}
          <li role="none">
            <Link
              to="/settings/mailboxes/"
              className="flex w-full items-center gap-2 px-3 py-2.5 text-left font-mono text-[11px] text-theme-text transition-colors hover:bg-theme-panel"
              onClick={() => setOpen(false)}
            >
              <span className="inline-flex w-5 shrink-0 items-center justify-center" aria-hidden="true">
                <span className="material-symbols-outlined text-[16px] leading-none text-theme-dim">
                  add
                </span>
              </span>
              <span className="min-w-0 flex-1">New Mailbox</span>
            </Link>
          </li>
        </ul>
      ) : null}
    </div>
  );
}
